import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/dashboards/StudentDashboard.css';
import '../../styles/dashboards/StudentDashboard-simplified.css';

const StudentDashboard = ({ data }) => {
  // Get user from context
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    assignmentCompletion: 0,
    assessmentAverage: { value: 0, text: 'N/A' },
    attendanceRate: 0
  });
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [completedAssignments, setCompletedAssignments] = useState([]);
  // Center data state
  const [centerData, setCenterData] = useState(null);

  // Fetch center data
  useEffect(() => {
    const fetchCenterData = async () => {
      if (user && user.center) {
        try {
          const response = await api.get(`/centers/${user.center}`);
          setCenterData(response.data.data || response.data);
        } catch (error) {
          console.error('Error fetching center data:', error);
        }
      }
    };
    
    fetchCenterData();
  }, [user]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch assignments - add logging for debugging
        console.log('Fetching student assignments...');
        const assignmentsResponse = await api.get('/assignments/student');
        console.log('Assignments response:', assignmentsResponse);
        
        // Handle different API response formats
        let assignmentsData = [];
        if (assignmentsResponse.data) {
          if (Array.isArray(assignmentsResponse.data)) {
            assignmentsData = assignmentsResponse.data;
          } else if (assignmentsResponse.data.data && Array.isArray(assignmentsResponse.data.data)) {
            assignmentsData = assignmentsResponse.data.data;
          } else if (assignmentsResponse.data.assignments && Array.isArray(assignmentsResponse.data.assignments)) {
            assignmentsData = assignmentsResponse.data.assignments;
          }
        }
        
        console.log('Processed assignments data:', assignmentsData);
        
        // Fetch attendance - with null check for user
        let attendanceData = [];
        if (user && user.id) {
          try {
            const attendanceResponse = await api.get(`/attendance/student/${user.id}`);
            attendanceData = attendanceResponse.data.data || [];
          } catch (attendanceErr) {
            console.error('Error fetching attendance:', attendanceErr);
            // Continue with empty attendance data
          }
        } else {
          console.log('User ID not available for attendance fetch');
        }
        
        // Ensure assignmentsData is actually an array before filtering
        if (!Array.isArray(assignmentsData)) {
          console.error('assignmentsData is not an array:', assignmentsData);
          assignmentsData = [];
        }
        
        console.log('Filtering assignments, total count:', assignmentsData.length);
        
        // Process assignments - with more inclusive filtering to catch different status formats
        const active = assignmentsData.filter(assignment => {
          const status = assignment.status?.toLowerCase();
          return status === 'active' || status === 'pending' || 
            (assignment.assessment && assignment.assessment.rating === 'Need to Repeat');
        });
        
        console.log('Active assignments count:', active.length);
        
        const completed = assignmentsData
          .filter(assignment => {
            const status = assignment.status?.toLowerCase();
            return status === 'completed' && 
              (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat');
          })
          .sort((a, b) => {
            if (a.assessment?.assessedAt && b.assessment?.assessedAt) {
              return new Date(b.assessment.assessedAt) - new Date(a.assessment.assessedAt);
            }
            return 0;
          })
          .slice(0, 5);
          
        console.log('Completed assignments count:', completed.length);
        
        setActiveAssignments(active);
        setCompletedAssignments(completed);
        
        // Calculate stats
        calculateMetrics(assignmentsData, attendanceData);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [user]);

  // Calculate metrics based on data
  const calculateMetrics = (assignmentsData, attendanceData) => {
    if (!assignmentsData) assignmentsData = [];
    if (!attendanceData) attendanceData = [];
    
    // Calculate assignment completion rate
    const totalAssignments = assignmentsData.length;
    const completedCount = assignmentsData.filter(assignment => 
      assignment.status === 'completed' &&
      (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
    ).length;
    
    const completionRate = totalAssignments > 0 
      ? Math.round((completedCount / totalAssignments) * 100) 
      : 0;
    
    // Calculate assessment average
    const assessedAssignments = assignmentsData.filter(assignment => 
      assignment.assessment && assignment.assessment.rating
    );
    
    let assessmentAvgValue = 0;
    let assessmentText = 'N/A';
    
    if (assessedAssignments.length > 0) {
      // Map ratings to numerical values
      const ratingValues = {
        'Excellent': 5,
        'Very Good': 4,
        'Good': 3,
        'Pass': 2,
        'Need to Repeat': 1
      };
      
      const assessmentSum = assessedAssignments.reduce((sum, assignment) => {
        return sum + (ratingValues[assignment.assessment.rating] || 0);
      }, 0);
      
      assessmentAvgValue = assessmentSum / assessedAssignments.length;
      
      // Determine text rating based on average
      if (assessmentAvgValue >= 4.5) assessmentText = 'Excellent';
      else if (assessmentAvgValue >= 3.5) assessmentText = 'Very Good';
      else if (assessmentAvgValue >= 2.5) assessmentText = 'Good';
      else if (assessmentAvgValue >= 1.5) assessmentText = 'Pass';
      else assessmentText = 'Need to Repeat';
    }
    
    // Calculate attendance rate
    let attendanceRate = 0;
    
    if (Array.isArray(attendanceData) && attendanceData.length > 0) {
      const totalSessions = attendanceData.length;
      const attendedSessions = attendanceData.filter(record => 
        record.status.toLowerCase() === 'present' || record.status.toLowerCase() === 'late'
      ).length;
      
      attendanceRate = totalSessions > 0 
        ? Math.round((attendedSessions / totalSessions) * 100) 
        : 0;
    } else if (attendanceData && attendanceData.stats && attendanceData.stats.attendanceRate) {
      // If stats are provided directly (parent endpoint)
      attendanceRate = parseInt(attendanceData.stats.attendanceRate.replace('%', '')) || 0;
    }
    
    // Update stats
    setDashboardStats({
      assignmentCompletion: completionRate,
      assessmentAverage: { 
        value: assessmentAvgValue > 0 ? parseFloat(assessmentAvgValue.toFixed(1)) : 0, 
        text: assessmentText 
      },
      attendanceRate: attendanceRate
    });
  };

  // Format assignment title
  const formatAssignmentTitle = (assignment) => {
    if (assignment.title) return assignment.title;
    if (assignment.surah && assignment.verses) {
      return `${assignment.surah.name} (${assignment.verses.from}-${assignment.verses.to})`;
    }
    return 'Unknown Assignment';
  };

  // Format due date
  const formatDueDate = (dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Get assessment styles
  const getAssessmentStyles = (rating) => {
    switch(rating) {
      case 'Excellent':
        return { color: '#2e7d32', backgroundColor: '#e8f5e9' };
      case 'Very Good':
        return { color: '#2e7d32', backgroundColor: '#e8f5e9' };
      case 'Good':
        return { color: '#ff8f00', backgroundColor: '#fff8e1' };
      case 'Pass':
        return { color: '#0288d1', backgroundColor: '#e1f5fe' };
      case 'Need to Repeat':
        return { color: '#d32f2f', backgroundColor: '#ffebee' };
      default:
        return { color: '#757575', backgroundColor: '#f5f5f5' };
    }
  };

  if (loading) {
    return (
      <div className="student-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-dashboard-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  // Center data is fetched in useEffect above

  return (
    <div className="student-dashboard">
      {/* Dashboard Header */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '15px'}}>
        <div style={{backgroundColor: '#4a90e2', borderRadius: '8px', padding: '6px 10px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
          <h1 style={{fontSize: '16px', margin: 0, color: 'Black', fontWeight: '500'}}>Student Dashboard</h1>
        </div>
        
      </div>
      
      {/* Welcome Section */}
      <div className="welcome-section">
        <h1>Welcome, {user?.name || 'Student'}</h1>
      </div>
      
      {/* Stats Container - mobile style */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{dashboardStats.assignmentCompletion}%</div>
          <div className="stat-label">Assignments</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-value">{dashboardStats.assessmentAverage.value}</div>
          <div className="stat-label">{dashboardStats.assessmentAverage.text}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-value">{dashboardStats.attendanceRate}%</div>
          <div className="stat-label">Attendance</div>
        </div>
      </div>
      
      {/* Active Assignments Section */}
      <div className="assignments-section">
        <h2>Active Assignments</h2>
        {activeAssignments.length > 0 ? (
          <div className="assignments-list">
            {activeAssignments.map((assignment, index) => {
              // Check if assignment is overdue
              const dueDate = new Date(assignment.dueDate);
              const isOverdue = dueDate < new Date() && assignment.status !== 'completed';
              
              return (
                <Link 
                  to={`/assignments/student-view/${assignment._id}`} 
                  className="assignment-card"
                  key={`active-${index}`}
                >
                  {/* Extremely simplified card with just borders and title */}
                  <div className="blue-border"></div>
                  <div className={`status-border ${isOverdue ? 'red' : 'green'}`}></div>
                  <div className="title-box">
                    {formatAssignmentTitle(assignment)}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <p>No active assignments found</p>
            <p className="empty-state-message">
              {error ? 
                'There was an error loading assignments. Please try again later.' : 
                'Your teacher has not assigned any memorization tasks yet.'
              }
            </p>
          </div>
        )}
      </div>
      

      {/* Navigation Buttons */}
      <div className="navigation-buttons">
        <Link to="/assignments/student" className="nav-button">
          View All Assignments
        </Link>
        <Link to="/attendance" className="nav-button">
          View Your Attendance
        </Link>
      </div>
    </div>
  );
};

export default StudentDashboard;